import { useCallback, useState } from 'react';
import { apiFetch } from '../utils/api-fetch';
import { apiErrorMessage, parseApiJson } from '../utils/api-json';

export type DeliverySummary = {
  byStatus: Record<string, number>;
  total: number;
  health?: {
    sendPaused: boolean;
    pendingRetryJobs?: number;
    processingRetryJobs?: number;
  };
};

export type DeliveryRecipient = {
  id: string;
  recipient_email: string;
  delivery_status: string | null;
  subject?: string | null;
  sent_at: string;
  last_event?: string | null;
};

export type RetryJob = {
  id: string;
  recipient_id: string;
  status: string;
  due_at: string;
  attempt_number: number;
  last_error: string | null;
};

export type SuppressionRow = {
  id: string;
  email_normalized: string;
  reason: string;
  scope: string;
  site_id: string | null;
  updated_at: string;
};

export function useEmailDelivery(siteId: string) {
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async (): Promise<DeliverySummary | null> => {
    const q = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
    const res = await apiFetch(`/api/email-delivery/summary${q}`);
    const data = await parseApiJson(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Could not load summary'));
    }
    return data as DeliverySummary;
  }, [siteId]);

  const fetchRecipients = useCallback(
    async (params?: { deliveryStatus?: string; email?: string; offset?: number }) => {
      const search = new URLSearchParams({ limit: '50' });
      if (siteId) {
        search.set('siteId', siteId);
      }
      if (params?.deliveryStatus) {
        search.set('deliveryStatus', params.deliveryStatus);
      }
      if (params?.email) {
        search.set('email', params.email);
      }
      if (params?.offset) {
        search.set('offset', String(params.offset));
      }
      const res = await apiFetch(`/api/email-delivery/recipients?${search}`);
      const data = await parseApiJson(res);
      if (!res.ok) {
        throw new Error(apiErrorMessage(data, 'Could not load recipients'));
      }
      return data as { rows: DeliveryRecipient[]; total: number };
    },
    [siteId]
  );

  const fetchRecipientDetail = useCallback(async (id: string) => {
    const res = await apiFetch(`/api/email-delivery/recipients/${id}`);
    const data = await parseApiJson<{
      recipient: DeliveryRecipient | null;
      events: Array<Record<string, unknown>>;
      retryJobs: Array<Record<string, unknown>>;
    }>(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Could not load recipient'));
    }
    return data;
  }, []);

  const fetchRetries = useCallback(async () => {
    const search = new URLSearchParams({ limit: '50' });
    if (siteId) {
      search.set('siteId', siteId);
    }
    const res = await apiFetch(`/api/email-delivery/retries?${search}`);
    const data = await parseApiJson(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Could not load retries'));
    }
    return data as { jobs: RetryJob[] };
  }, [siteId]);

  const fetchSuppressions = useCallback(async (email?: string) => {
    const search = new URLSearchParams({ limit: '50' });
    if (siteId) {
      search.set('siteId', siteId);
    }
    if (email) {
      search.set('email', email);
    }
    const res = await apiFetch(`/api/email-delivery/suppressions?${search}`);
    const data = await parseApiJson(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Could not load suppressions'));
    }
    return data as { rows: SuppressionRow[] };
  }, [siteId]);

  const runPreflight = useCallback(
    async (emails: string[]) => {
      const res = await apiFetch('/api/email-delivery/preflight', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, siteId: siteId || undefined }),
      });
      const data = await parseApiJson<{
        rows: Array<{ email: string; sendable: boolean; reason?: string }>;
        sendable: number;
        total: number;
      }>(res);
      if (!res.ok) {
        throw new Error(apiErrorMessage(data, 'Preflight failed'));
      }
      return data;
    },
    [siteId]
  );

  const queueRetries = useCallback(async (recipientIds: string[], dryRun: boolean) => {
    const res = await apiFetch('/api/email-delivery/retries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipientIds, dryRun }),
    });
    const data = await parseApiJson<{
      items?: Array<{ eligible: boolean }>;
      eligibleCount?: number;
      created?: number;
    }>(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Retry request failed'));
    }
    return data;
  }, []);

  const cancelRetry = useCallback(async (jobId: string) => {
    const res = await apiFetch(`/api/email-delivery/retries/${jobId}/cancel`, {
      method: 'POST',
    });
    const data = await parseApiJson(res);
    if (!res.ok) {
      throw new Error(apiErrorMessage(data, 'Could not cancel retry'));
    }
    return data;
  }, []);

  const reviewSuppression = useCallback(
    async (suppressionId: string, action: 'deactivate' | 'confirm', note?: string) => {
      const res = await apiFetch(`/api/email-delivery/suppressions/${suppressionId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, note }),
      });
      const data = await parseApiJson(res);
      if (!res.ok) {
        throw new Error(apiErrorMessage(data, 'Review failed'));
      }
      return data;
    },
    []
  );

  return {
    loading,
    setLoading,
    fetchSummary,
    fetchRecipients,
    fetchRecipientDetail,
    fetchRetries,
    fetchSuppressions,
    runPreflight,
    queueRetries,
    cancelRetry,
    reviewSuppression,
  };
}
